import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';

const KEY = 'duti_voice_replies';
const LANG_KEY = 'duti_voice_lang';
const silentWav = () => {
  const n = 800;
  const buf = new ArrayBuffer(44 + n);
  const v = new DataView(buf);
  const w = (o, str) => [...str].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  w(0, 'RIFF'); v.setUint32(4, 36 + n, true); w(8, 'WAVE'); w(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, 8000, true); v.setUint32(28, 8000, true); v.setUint16(32, 1, true); v.setUint16(34, 8, true);
  w(36, 'data'); v.setUint32(40, n, true);
  for (let i = 0; i < n; i += 1) v.setUint8(44 + i, 128);
  return URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
};
let SILENT = null;

export const useSpokenReplies = () => {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(KEY) === 'on');
  const enabledRef = useRef(enabled);
  const [playing, setPlaying] = useState(null);
  const [language, setLanguage] = useState(() => localStorage.getItem(LANG_KEY) || 'en');
  const langRef = useRef(language);
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  const el = () => {
    if (!audioRef.current) audioRef.current = new Audio();
    return audioRef.current;
  };

  const stop = useCallback(() => {
    audioRef.current?.pause();
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    setPlaying(null);
  }, []);

  useEffect(() => stop, [stop]);

  const toggle = () => {
    const next = !enabledRef.current;
    localStorage.setItem(KEY, next ? 'on' : 'off');
    enabledRef.current = next;
    setEnabled(next);
    if (next) prime();
    else stop();
  };

  const switchLanguage = () => {
    const next = langRef.current === 'en' ? 'tw' : 'en';
    localStorage.setItem(LANG_KEY, next);
    langRef.current = next;
    setLanguage(next);
    stop();
  };

  // Called inside a tap so mobile browsers allow the later, delayed playback.
  const prime = () => {
    if (!enabledRef.current) return;
    const a = el();
    SILENT = SILENT || silentWav();
    a.src = SILENT;
    a.play().catch(() => {});
  };

  const play = useCallback(async (text, id) => {
    stop();
    if (!text?.trim()) return;
    setPlaying(id ?? 'auto');
    try {
      const { data } = await api.post('/duti/speak', { text, language: langRef.current }, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      urlRef.current = url;
      const a = el();
      a.onended = () => stop();
      a.src = url;
      await a.play();
    } catch (e) {
      setPlaying(null);
      let msg = "Couldn't read that aloud";
      try { msg = JSON.parse(await e.response?.data?.text())?.detail || msg; } catch { /* keep default */ }
      toast.error(msg);
    }
  }, [stop]);

  const autoPlay = (text, id) => { if (enabledRef.current && text) play(text, id); };

  return { enabled, toggle, play, stop, playing, prime, autoPlay, language, switchLanguage };
};
